import { createStore } from 'vuex'

const store = createStore({
  state() {
    return {
      isLoading: false,
      loadingMessage: '',
      sessionToken: localStorage.getItem('title_session_token') || null,
      settlementAgent: JSON.parse(localStorage.getItem('settlement_agent_info')) || null,
      loan: JSON.parse(localStorage.getItem('title_loan_info')) || null,
      closingRequest: JSON.parse(localStorage.getItem('closing_request_info')) || null,
      loanOfficer: JSON.parse(localStorage.getItem('title_loan_officer_info')) || null,
      
      // Fee data
      fees: [],
      toleranceStatus: null,
      
      // Payoff data
      payoffs: [],
      
      // Title info data
      titleInfo: null,
      
      // Closing progress (persisted to localStorage)
      closingProgress: JSON.parse(localStorage.getItem('closing_progress')) || {
        titleInfoComplete: false,
        feesComplete: false,
        payoffsComplete: false,
        previewGenerated: false,
        documentsGenerated: false,
      },
      
      error: null,
    }
  },
  
  mutations: {
    SET_LOADING(state, { isLoading, message = '' }) {
      state.isLoading = isLoading
      state.loadingMessage = message
    },
    
    SET_SESSION(state, { sessionToken, settlementAgent, loan, closingRequest }) {
      state.sessionToken = sessionToken
      state.settlementAgent = settlementAgent
      state.loan = loan
      state.closingRequest = closingRequest
      
      localStorage.setItem('title_session_token', sessionToken)
      localStorage.setItem('settlement_agent_info', JSON.stringify(settlementAgent))
      localStorage.setItem('title_loan_info', JSON.stringify(loan))
      localStorage.setItem('closing_request_info', JSON.stringify(closingRequest))
    },
    
    SET_LOAN(state, loan) {
      state.loan = loan
      localStorage.setItem('title_loan_info', JSON.stringify(loan))
    },
    
    SET_LOAN_OFFICER(state, loanOfficer) {
      state.loanOfficer = loanOfficer
      localStorage.setItem('title_loan_officer_info', JSON.stringify(loanOfficer))
    },
    
    SET_CLOSING_REQUEST(state, closingRequest) {
      state.closingRequest = closingRequest
      localStorage.setItem('closing_request_info', JSON.stringify(closingRequest))
    },
    
    SET_FEES(state, fees) {
      state.fees = fees
    },
    
    SET_TOLERANCE_STATUS(state, toleranceStatus) {
      state.toleranceStatus = toleranceStatus
    },
    
    SET_PAYOFFS(state, payoffs) {
      state.payoffs = payoffs
    },
    
    SET_TITLE_INFO(state, titleInfo) {
      state.titleInfo = titleInfo
    },
    
    SET_CLOSING_PROGRESS(state, progress) {
      state.closingProgress = { ...state.closingProgress, ...progress }
      localStorage.setItem('closing_progress', JSON.stringify(state.closingProgress))
    },
    
    SET_ERROR(state, error) {
      state.error = error
    },
    
    CLEAR_SESSION(state) {
      state.sessionToken = null
      state.settlementAgent = null
      state.loan = null
      state.closingRequest = null
      state.loanOfficer = null
      state.fees = []
      state.toleranceStatus = null
      state.payoffs = []
      state.titleInfo = null
      state.closingProgress = {
        titleInfoComplete: false,
        feesComplete: false,
        payoffsComplete: false,
        previewGenerated: false,
        documentsGenerated: false,
      }
      
      localStorage.removeItem('title_session_token')
      localStorage.removeItem('settlement_agent_info')
      localStorage.removeItem('title_loan_info')
      localStorage.removeItem('closing_request_info')
      localStorage.removeItem('title_loan_officer_info')
      localStorage.removeItem('closing_progress')
    },
  },
  
  actions: {
    setLoading({ commit }, payload) {
      if (typeof payload === 'boolean') {
        commit('SET_LOADING', { isLoading: payload })
      } else {
        commit('SET_LOADING', payload)
      }
    },
    
    setSession({ commit }, sessionData) {
      commit('SET_SESSION', sessionData)
    },
    
    setLoan({ commit }, loan) {
      commit('SET_LOAN', loan)
    },
    
    setLoanOfficer({ commit }, loanOfficer) {
      commit('SET_LOAN_OFFICER', loanOfficer)
    },
    
    setClosingRequest({ commit }, closingRequest) {
      commit('SET_CLOSING_REQUEST', closingRequest)
    },
    
    setFees({ commit }, fees) {
      commit('SET_FEES', fees)
    },
    
    setToleranceStatus({ commit }, toleranceStatus) {
      commit('SET_TOLERANCE_STATUS', toleranceStatus)
    },
    
    setPayoffs({ commit }, payoffs) {
      commit('SET_PAYOFFS', payoffs)
    },
    
    setTitleInfo({ commit }, titleInfo) {
      commit('SET_TITLE_INFO', titleInfo)
    },
    
    setClosingProgress({ commit }, progress) {
      commit('SET_CLOSING_PROGRESS', progress)
    },
    
    setError({ commit }, error) {
      commit('SET_ERROR', error)
    },
    
    clearSession({ commit }) {
      commit('CLEAR_SESSION')
    },
  },
  
  getters: {
    isLoading: (state) => state.isLoading,
    loadingMessage: (state) => state.loadingMessage,
    isAuthenticated: (state) => !!state.sessionToken,
    sessionToken: (state) => state.sessionToken,
    settlementAgent: (state) => state.settlementAgent,
    loan: (state) => state.loan,
    closingRequest: (state) => state.closingRequest,
    loanOfficer: (state) => state.loanOfficer,
    fees: (state) => state.fees,
    toleranceStatus: (state) => state.toleranceStatus,
    payoffs: (state) => state.payoffs,
    titleInfo: (state) => state.titleInfo,
    closingProgress: (state) => state.closingProgress,
    error: (state) => state.error,
    
    // Computed getters
    propertyAddress: (state) => {
      if (state.loan?.property) {
        const p = state.loan.property
        return `${p.address_line_1 || ''}, ${p.city || ''}, ${p.state || ''} ${p.zip_code || ''}`.trim()
      }
      return ''
    },
    
    borrowerNames: (state) => state.loan?.borrower_names || '',
    
    hasToleranceViolation: (state) => {
      return state.toleranceStatus?.has_violation || false
    },
    
    hasToleranceWarning: (state) => {
      return state.toleranceStatus?.has_warning || false
    },
    
    overallProgress: (state) => {
      const steps = [
        state.closingProgress.titleInfoComplete,
        state.closingProgress.feesComplete,
        state.closingProgress.payoffsComplete,
        state.closingProgress.previewGenerated,
      ]
      const completed = steps.filter(Boolean).length
      return Math.round((completed / steps.length) * 100)
    },
  },
})

export default store

